import React, { useState, useRef, useContext, forwardRef, useImperativeHandle } from "react";
import { useTheme } from "@material-ui/core/styles";
import {
    Box,
    Typography,
    TextField,
    IconButton,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Paper,
    Stack,
    Collapse,
    CircularProgress,
    Switch,
    Tooltip,
    FormControlLabel,
    Chip
} from "@mui/material";
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Schedule as ScheduleIcon,
    Image as ImageIcon,
    Videocam as VideoIcon,
    MicNone as AudioIcon,
    AttachFile as FileIcon,
    Subject as TextIcon,
    AccessTime as IntervalIcon,
    Edit as EditIcon,
    InfoOutlined as InfoOutlinedIcon
} from "@mui/icons-material";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../ConfirmationModal";
import { validateVariableName, validateUniqueVariable } from "../../utils/variableValidator";

const Thumbnail = ({ item }) => {
    const { user } = useContext(AuthContext);
    const companyId = user?.companyId;
    const [pathIndex, setPathIndex] = useState(0);

    if (!companyId) return null;

    if (item.type === 'video') {
        return <VideoIcon sx={{ fontSize: 20, color: '#666' }} />;
    }

    const filename = item.value;
    const safeFilename = filename ? (typeof filename === 'string' ? encodeURIComponent(filename) : encodeURIComponent(filename.uploadedName || "")) : "";
    const paths = [
        `/public/company${companyId}/flow/${safeFilename}`,
        `/public/company${companyId}/${safeFilename}`
    ];

    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const baseUrl = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;

    // Try local preview first if exists
    if (item.tempUrl && pathIndex === 0) {
        return (
            <img
                src={item.tempUrl}
                alt="local-preview"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={() => setPathIndex(1)}
            />
        );
    }

    // Fallback logic
    if (pathIndex >= (item.tempUrl ? paths.length + 1 : paths.length)) {
        return <ImageIcon sx={{ fontSize: 20, color: '#ccc' }} />;
    }

    const currentPath = item.tempUrl ? paths[pathIndex - 1] : paths[pathIndex];

    return (
        <img
            src={`${baseUrl}${currentPath}`}
            alt="thumb"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={() => setPathIndex(prev => prev + 1)}
        />
    );
};

const RemarketingTaskEditor = forwardRef(({ tasks = [], onChange, maxTasks = 30, isAiNode = false, enableAntiBan = false, title }, ref) => {
    const theme = useTheme();
    const isDark = (theme?.palette?.type === "dark") || (typeof window !== "undefined" && window.localStorage?.getItem("preferredTheme") === "dark");
    const whitelabelColor = theme?.palette?.primary?.main || localStorage.getItem("primaryColorLight") || localStorage.getItem("primaryColorDark") || "#10B981";

    // Theme-adaptive design tokens
    const cardBg = isDark ? "#23262B" : "#FFFFFF";
    const cardBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)";
    const cardHeaderBg = isDark ? "rgba(255, 255, 255, 0.04)" : "#F8FAFC";
    const cardCollapseBg = isDark ? "rgba(0, 0, 0, 0.28)" : "#F8FAFC";
    const textPrimary = isDark ? "#FFFFFF" : "#0F172A";
    const textSecondary = isDark ? "#CBD5E1" : "#475569";
    const textMuted = isDark ? "#94A3B8" : "#64748B";
    const inputBg = isDark ? "rgba(0, 0, 0, 0.25)" : "#FFFFFF";
    const inputBorder = isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.23)";
    const editorBg = isDark ? "rgba(255, 255, 255, 0.02)" : "#FFFFFF";

    const [expandedTask, setExpandedTask] = useState(null);
    const [isAntiBan, setIsAntiBan] = useState(false);
    const [isAutoRemarketing, setIsAutoRemarketing] = useState(false);
    const [autoRemarketingType, setAutoRemarketingType] = useState("contextual"); // "contextual" | "angle"
    const [promptGuidance, setPromptGuidance] = useState("");
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    // New State for the "Unified Editor" - Current building task
    const [waitTime, setWaitTime] = useState(1);
    const [waitUnit, setWaitUnit] = useState("days");
    const [content, setContent] = useState([]); // Array of { type: 'text'|'image'..., value: '...', originalName: '...', tempUrl: '...' }
    const [loadingUpload, setLoadingUpload] = useState(false);

    // [ANTIGRAVITY] Variable field for each task
    const [taskVariable, setTaskVariable] = useState("");
    const [variableError, setVariableError] = useState(false);

    const [editingTaskId, setEditingTaskId] = useState(null); // [ANTIGRAVITY] Track editing state

    // [ANTIGRAVITY] Expose method to get pending task via ref
    useImperativeHandle(ref, () => ({
        getPendingTask: () => {
            if (content.length > 0 || isAutoRemarketing) {
                let cleanContent = [];
                if (isAutoRemarketing) {
                    cleanContent = [{
                        type: autoRemarketingType === 'contextual' ? 'auto_contextual' : 'auto_angle',
                        value: autoRemarketingType === 'contextual' ? (promptGuidance || '') : '',
                        autoRemarketingType: autoRemarketingType,
                        promptGuidance: autoRemarketingType === 'contextual' ? (promptGuidance || '') : ''
                    }];
                } else {
                    cleanContent = [...content];
                }
                return {
                    id: editingTaskId || Date.now(),
                    waitTime: Number(waitTime),
                    waitUnit: waitUnit,
                    content: cleanContent,
                    variable: taskVariable,
                    antiBan: isAntiBan,
                    autoRemarketing: isAutoRemarketing,
                    autoRemarketingType: isAutoRemarketing ? autoRemarketingType : undefined,
                    promptGuidance: isAutoRemarketing && autoRemarketingType === 'contextual' ? promptGuidance : undefined,
                    order: tasks.length + 1 // Provisional order
                };
            }
            return null;
        },
        clearPendingTask: () => {
            resetEditor();
        },
        hasPendingChanges: () => {
            // [ANTIGRAVITY] Check if there's unsaved content
            return content.length > 0 || (isAutoRemarketing && editingTaskId !== null);
        }
    }));

    const resetEditor = () => {
        setContent([]);
        setWaitTime(1);
        setWaitUnit('days');
        setTaskVariable("");
        setVariableError(false);
        setEditingTaskId(null);
        setIsAutoRemarketing(false);
        setAutoRemarketingType("contextual");
        setPromptGuidance("");
    };

    const handleToggleAutoRemarketing = (checked) => {
        setIsAutoRemarketing(checked);
        if (checked) {
            setContent([]);
            setAutoRemarketingType("contextual");
            setPromptGuidance("");
        } else {
            setAutoRemarketingType("contextual");
            setPromptGuidance("");
        }
    };

    const handleChangeAutoType = async (newType) => {
        if (newType === "angle") {
            try {
                const { data } = await api.get("/ai-knowledge");
                const hasRemarketingRAG = data.some(k => k.type && k.type.toLowerCase() === 'remarketing');
                if (!hasRemarketingRAG) {
                    toast.warning(i18n.t("flowBuilder.remarketing.noRagAnglesWarning") || "No tienes ningún 'Ángulo de Venta' en tu Base de Conocimiento de IA. Recuerda subir al menos uno para que este modo funcione.");
                }
            } catch (err) {
                console.error(err);
            }
        }
        setAutoRemarketingType(newType);
    };

    // Refs for file inputs
    const imageInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const audioInputRef = useRef(null);
    const docInputRef = useRef(null);
    const editorRef = useRef(null);

    // --- Actions ---
    const handleAddText = () => {
        setContent([...content, { type: "text", value: "" }]);
    };

    const handleInterval = () => {
        setContent([...content, { type: "interval", value: "2" }]);
    };

    const handleFileUpload = async (e, type) => {
        const target = e.target;
        const file = target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("typeArch", "flow"); // MUST BE FIRST for multer to see it in req.body
        formData.append("medias", file);
        formData.append("body", file.name);

        setLoadingUpload(true);
        try {
            const { data } = await api.post("/flowbuilder/content?typeArch=flow", formData);
            console.log("[REMARKETING] Upload success, received:", data);
            const uploadedName = data.uploadedName || (Array.isArray(data) ? data[0] : data);

            setContent((prevContent) => [
                ...prevContent,
                {
                    type: type,
                    value: uploadedName, // The internal server filename
                    originalName: file.name,
                    tempUrl: URL.createObjectURL(file) // Local blob for instant preview
                }
            ]);
            toast.success(i18n.t("flowBuilder.singleBlock.toasts.saveSuccess") || "Contenido guardado con éxito");
        } catch (err) {
            console.error(err);
            toastError(err);
        }
        setLoadingUpload(false);
        // Reset input using the stored reference
        target.value = "";
    };

    const handleRemoveContentItem = (index) => {
        setContent(prev => {
            const newContent = [...prev];
            newContent.splice(index, 1);
            return newContent;
        });
    };

    const handleTextChange = (index, newValue) => {
        setContent(prev => {
            const newContent = [...prev];
            newContent[index].value = newValue;
            return newContent;
        });
    };

    const handleIntervalChange = (index, newValue) => {
        setContent(prev => {
            const newContent = [...prev];
            newContent[index].value = newValue;
            return newContent;
        });
    };

    // [ANTIGRAVITY] Handler para validar variable de tarea en tiempo real
    const handleTaskVariableChange = (e) => {
        const value = e.target.value;
        setTaskVariable(value);
    };

    const handleAddTask = () => {
        if (!editingTaskId && tasks.length >= maxTasks) return;

        // Validar contenido si no es auto remarketing
        if (!isAutoRemarketing && content.length === 0) {
            toast.error("Agregue al menos un contenido (texto o multimedia).");
            return;
        }

        let cleanContent = [];
        if (isAutoRemarketing) {
            cleanContent = [{
                type: autoRemarketingType === 'contextual' ? 'auto_contextual' : 'auto_angle',
                value: autoRemarketingType === 'contextual' ? (promptGuidance || '') : '',
                autoRemarketingType: autoRemarketingType,
                promptGuidance: autoRemarketingType === 'contextual' ? (promptGuidance || '') : ''
            }];
        } else {
            // [ANTIGRAVITY FIX] Strip tempUrl (blob:// local preview) before saving to DB
            cleanContent = content.map(item => {
                const { tempUrl, ...cleanItem } = item;
                // Also ensure value is always a string (not an object from API response)
                if (cleanItem.value !== null && cleanItem.value !== undefined && typeof cleanItem.value !== 'string') {
                    cleanItem.value = typeof cleanItem.value === 'object' 
                        ? (cleanItem.value.uploadedName || cleanItem.value.name || JSON.stringify(cleanItem.value))
                        : String(cleanItem.value);
                }
                return cleanItem;
            });
        }

        const newTaskBase = {
            waitTime: Number(waitTime),
            waitUnit: waitUnit,
            content: cleanContent,
            variable: taskVariable,
            antiBan: isAntiBan,
            autoRemarketing: isAutoRemarketing,
            autoRemarketingType: isAutoRemarketing ? autoRemarketingType : undefined,
            promptGuidance: isAutoRemarketing && autoRemarketingType === 'contextual' ? promptGuidance : undefined,
        };

        let updatedTasks;

        if (editingTaskId) {
            // [ANTIGRAVITY] UPDATE Existing Task logic refactored
            console.log("[REMARKETING EDITOR] Updating task ID:", editingTaskId);
            
            // Verify existence
            const taskExists = tasks.some(t => t.id === editingTaskId);
            if (taskExists) {
                updatedTasks = tasks.map(t =>
                    t.id === editingTaskId ? { ...t, ...newTaskBase } : t
                );
                toast.success("Tarea actualizada correctamente");
            } else {
                // Fallback (lost ID): Treat as new
                const newTask = { id: Date.now(), ...newTaskBase };
                updatedTasks = [...tasks, newTask];
                toast.success("Tarea agregada (no se pudo actualizar existente)");
            }
        } else {
            // [ANTIGRAVITY] CREATE New Task
            const newTask = {
                id: Date.now(),
                active: true,
                ...newTaskBase
            };
            updatedTasks = [...tasks, newTask];
            toast.success("Tarea agregada a la lista");
        }

        // [ANTIGRAVITY] FINAL SAFETY: Deduplicate by ID to prevent React Key collisions
        // Keep the LAST occurrence if duplicates exist (which shouldn't happen with correct logic, but for safety)
        const uniqueMap = new Map();
        updatedTasks.forEach(t => uniqueMap.set(t.id, t));
        const uniqueTasks = Array.from(uniqueMap.values());

        // [ANTIGRAVITY] Re-assign Orders sequentially
        const orderedTasks = uniqueTasks.map((t, idx) => ({
            ...t,
            order: idx + 1
        }));

        // Notify parent
        onChange(orderedTasks);

        // Reset
        resetEditor();
    };

    const handleRemoveTask = (taskId) => {
        console.log("[REMARKETING] Removing task ID:", taskId);
        const filtered = tasks.filter((t) => t.id !== taskId);
        // [ANTIGRAVITY] Re-assign Orders after deletion
        const orderedTasks = filtered.map((t, idx) => ({ ...t, order: idx + 1 }));
        onChange(orderedTasks);

        // If we were editing the deleted task, reset editor
        if (editingTaskId === taskId) {
            resetEditor();
        }
    };

    const handleToggleActive = (taskId) => {
        const updatedTasks = tasks.map(t =>
            t.id === taskId ? { ...t, active: t.active === false ? true : false } : t
        );
        onChange(updatedTasks);
    };

    const handleEditTask = (task) => {
        // [ANTIGRAVITY] If there's already content in the buffer and it's not the same task
        if ((content.length > 0 || (isAutoRemarketing && !editingTaskId)) && editingTaskId !== task.id) {
            if (!window.confirm("Se perderá lo que estás editando actualmente. ¿Deseas editar esta tarea?")) {
                return;
            }
        }

        // Load task into builder buffer
        setWaitTime(task.waitTime || task.timeValue || 1);
        setWaitUnit(task.waitUnit || task.timeUnit || 'days');
        setIsAntiBan(!!task.antiBan);

        const isAuto = !!task.autoRemarketing || (task.content && task.content.some(c => c.type === 'auto_contextual' || c.type === 'auto_angle'));
        setIsAutoRemarketing(isAuto);

        if (isAuto) {
            let mode = task.autoRemarketingType;
            let guidance = task.promptGuidance || "";
            if (!mode && task.content) {
                const autoItem = task.content.find(c => c.type === 'auto_contextual' || c.type === 'auto_angle' || c.autoRemarketingType);
                if (autoItem) {
                    mode = autoItem.autoRemarketingType || (autoItem.type === 'auto_contextual' ? 'contextual' : 'angle');
                    guidance = autoItem.promptGuidance || autoItem.value || "";
                }
            }
            setAutoRemarketingType(mode || 'angle'); // Default retrocompatible
            setPromptGuidance(guidance);
            setContent([]);
        } else {
            setAutoRemarketingType("contextual");
            setPromptGuidance("");
            const loadedContent = task.content ? [...task.content] : [];
            // [ANTIGRAVITY] Fallback for old tasks that might not have content array
            if (!task.content && task.body) {
                loadedContent.push({ type: 'text', value: task.body });
            }
            setContent(loadedContent);
        }

        setTaskVariable(task.variable || "");
        setVariableError(false);

        // [ANTIGRAVITY] Set Editing Mode
        setEditingTaskId(task.id);

        // Scroll to editor (optional enhancement)
        setExpandedTask(null);
        setTimeout(() => editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    };

    const toggleExpand = (taskId) => {
        setExpandedTask(expandedTask === taskId ? null : taskId);
    };

    const getSummary = (task) => {
        const val = task.waitTime || task.timeValue || 0;
        const unit = task.waitUnit || task.timeUnit || 'days';
        const unitLabel = i18n.t(`flowBuilder.remarketing.units.${unit}`) || unit;
        const statusLabel = task.active === false ? " [INACTIVA]" : "";

        const isAuto = !!task.autoRemarketing || (task.content && task.content.some(c => c.type === 'auto_contextual' || c.type === 'auto_angle'));
        if (isAuto) {
            const isContextual = task.autoRemarketingType === 'contextual' || (task.content && task.content.some(c => c.type === 'auto_contextual'));
            const modeLabel = isContextual 
                ? (i18n.t("flowBuilder.remarketing.autoChipContextual") || "💬 Auto Contextual")
                : (i18n.t("flowBuilder.remarketing.autoChipAngle") || "🎯 Auto RAG (Ángulo)");
            return `Tarea: ${val} ${unitLabel} (${modeLabel})${statusLabel}`;
        }

        if (!task.content || task.content.length === 0) return `Tarea: ${val} ${unitLabel} - Sin contenido${statusLabel}`;

        const counts = task.content.reduce((acc, item) => {
            acc[item.type] = (acc[item.type] || 0) + 1;
            return acc;
        }, {});

        const summary = Object.entries(counts)
            .map(([type, count]) => `${count} ${i18n.t(`flowBuilder.remarketing.mediaTypes.${type}`) || type}`)
            .join(", ");

        return `Tarea: ${val} ${unitLabel} (${summary})${statusLabel}`;
    };

    const renderContentPreview = (item, index, readOnly = false) => {
        return (
            <Box key={index} sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 1,
                p: 1.2,
                border: '1px solid',
                borderColor: readOnly 
                    ? (isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)') 
                    : (isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'),
                borderRadius: 1.5,
                backgroundColor: readOnly 
                    ? (isDark ? 'rgba(255, 255, 255, 0.04)' : '#F1F5F9') 
                    : (isDark ? 'rgba(255, 255, 255, 0.02)' : '#FFFFFF')
            }}>
                {/* Icon based on type */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {item.type === 'text' && <TextIcon sx={{ color: whitelabelColor }} />}
                    {item.type === 'image' && <ImageIcon color="primary" />}
                    {item.type === 'video' && <VideoIcon color="secondary" />}
                    {item.type === 'audio' && <AudioIcon style={{ color: 'orange' }} />}
                    {item.type === 'document' && <FileIcon style={{ color: textMuted }} />}
                    {item.type === 'interval' && <IntervalIcon color="warning" />}
                </Box>

                {/* Thumbnail for Images/Videos */}
                {(item.type === 'image' || item.type === 'video') && item.value && (
                    <Box sx={{
                        width: 44,
                        height: 44,
                        minWidth: 44,
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: `1px solid ${cardBorder}`,
                        bgcolor: isDark ? 'rgba(0,0,0,0.3)' : '#F1F5F9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Thumbnail item={item} />
                    </Box>
                )}

                <Box sx={{ flex: 1 }}>
                    {/* Render Control */}
                    {item.type === 'text' && (
                        <TextField
                            fullWidth
                            multiline
                            minRows={1}
                            maxRows={6}
                            variant="outlined"
                            placeholder="Mensaje de texto..."
                            value={item.value}
                            onChange={(e) => !readOnly && handleTextChange(index, e.target.value)}
                            InputProps={{
                                readOnly: readOnly,
                                style: {
                                    color: textPrimary,
                                    fontSize: '0.875rem',
                                    lineHeight: '1.45',
                                    backgroundColor: readOnly 
                                        ? (isDark ? 'rgba(0, 0, 0, 0.45)' : '#F1F5F9') 
                                        : inputBg,
                                    borderRadius: '6px'
                                }
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: inputBorder,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: whitelabelColor,
                                    },
                                    '& textarea': {
                                        color: `${textPrimary} !important`,
                                    }
                                }
                            }}
                        />
                    )}

                    {item.type === 'interval' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ color: textSecondary }}>Esperar</Typography>
                            <TextField
                                type="number"
                                size="small"
                                disabled={readOnly}
                                value={item.value}
                                onChange={(e) => handleIntervalChange(index, e.target.value)}
                                sx={{ 
                                    width: 80,
                                    '& .MuiOutlinedInput-root': {
                                        color: textPrimary,
                                        bgcolor: inputBg,
                                        '& fieldset': { borderColor: inputBorder },
                                    }
                                }}
                            />
                            <Typography variant="body2" sx={{ color: textSecondary }}>segundos antes de continuar</Typography>
                        </Box>
                    )}

                    {item.type !== 'text' && item.type !== 'interval' && (
                        <Typography variant="body2" noWrap sx={{ maxWidth: 300, color: textPrimary }}>
                            {item.originalName || item.value}
                        </Typography>
                    )}
                </Box>

                {!readOnly && (
                    <IconButton size="small" onClick={() => handleRemoveContentItem(index)} color="error">
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
        );
    };

    return (
        <Box sx={{ width: "100%", mt: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 1, color: textPrimary }}>
                    <ScheduleIcon fontSize="small" sx={{ color: whitelabelColor }} />
                    {title || i18n.t("flowBuilder.remarketing.title") || "Tareas de Remarketing"}
                </Typography>
                <Typography variant="caption" sx={{ color: textSecondary }}>
                    {tasks.length} / {maxTasks} tareas
                </Typography>
            </Box>

            {/* Existing Tasks List */}
            <Stack spacing={1.5} sx={{ mb: 3 }}>
                {tasks.map((task, index) => (
                    <Paper
                        key={task.id || index}
                        elevation={0}
                        sx={{
                            borderRadius: 2,
                            overflow: "hidden",
                            border: "1px solid",
                            borderColor: editingTaskId === task.id ? "warning.main" : cardBorder,
                            bgcolor: cardBg,
                            boxShadow: isDark ? "none" : "0 1px 3px rgba(0, 0, 0, 0.05)"
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                p: 1.5,
                                bgcolor: task.active === false 
                                    ? (isDark ? "rgba(255, 255, 255, 0.02)" : "#F1F5F9") 
                                    : cardHeaderBg,
                                opacity: task.active === false ? 0.7 : 1,
                                cursor: "pointer",
                                borderLeft: task.active === false ? "4px solid #64748B" : `4px solid ${whitelabelColor}`
                            }}
                            onClick={() => toggleExpand(task.id)}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <Typography variant="caption" sx={{ fontWeight: "bold", color: task.active === false ? textMuted : whitelabelColor }}>
                                    #{index + 1}
                                </Typography>
                                <Typography variant="body2" sx={{ color: task.active === false ? textMuted : textPrimary, textDecoration: task.active === false ? "line-through" : "none", fontWeight: 500 }}>
                                    {getSummary(task)}
                                </Typography>
                                {task.antiBan && (
                                    <Chip size="small" label="🛡️ Anti-Ban" sx={{ ml: 1, backgroundColor: `${whitelabelColor}26`, color: whitelabelColor, fontWeight: 'bold', border: `1px solid ${whitelabelColor}4D` }} />
                                )}
                                {(task.autoRemarketing || (task.content && task.content.some(c => c.type === 'auto_contextual' || c.type === 'auto_angle'))) && (
                                    <Chip 
                                        size="small" 
                                        label={
                                            (task.autoRemarketingType === 'contextual' || (task.content && task.content.some(c => c.type === 'auto_contextual')))
                                                ? (i18n.t("flowBuilder.remarketing.autoChipContextual") || "💬 Auto Contextual")
                                                : (i18n.t("flowBuilder.remarketing.autoChipAngle") || "🎯 Auto RAG (Ángulo)")
                                        } 
                                        sx={{ 
                                            ml: 1, 
                                            backgroundColor: `${whitelabelColor}26`, 
                                            color: whitelabelColor, 
                                            border: `1px solid ${whitelabelColor}4D`,
                                            fontWeight: 'bold' 
                                        }} 
                                    />
                                )}
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Tooltip title={task.active !== false ? (i18n.t("flowBuilder.remarketing.deactivateTask") || "Desactivar tarea") : (i18n.t("flowBuilder.remarketing.activateTask") || "Activar tarea")}>
                                    <Switch
                                        size="small"
                                        checked={task.active !== false}
                                        onChange={(e) => { e.stopPropagation(); handleToggleActive(task.id); }}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{
                                            mr: 1,
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: whitelabelColor,
                                                '& + .MuiSwitch-track': { backgroundColor: whitelabelColor }
                                            }
                                        }}
                                    />
                                </Tooltip>
                                <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); handleEditTask(task); }}
                                    title={i18n.t("globalModals.remarketing.edit")}
                                >
                                    <EditIcon fontSize="small" sx={{ color: whitelabelColor }} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); handleRemoveTask(task.id); }}
                                    title={i18n.t("globalModals.remarketing.delete")}
                                    sx={{ color: textMuted, '&:hover': { color: 'error.main' } }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); toggleExpand(task.id); }}
                                    sx={{ color: textMuted }}
                                >
                                    {expandedTask === task.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                            </Box>
                        </Box>

                        <Collapse in={expandedTask === task.id}>
                            <Box sx={{ p: 2, bgcolor: cardCollapseBg, borderTop: `1px solid ${cardBorder}` }}>
                                {(task.autoRemarketing || (task.content && task.content.some(c => c.type === 'auto_contextual' || c.type === 'auto_angle'))) ? (
                                    <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: `${whitelabelColor}14`, border: `1px solid ${whitelabelColor}4D` }}>
                                        <Typography variant="subtitle2" sx={{ color: whitelabelColor, display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                                            {(task.autoRemarketingType === 'contextual' || (task.content && task.content.some(c => c.type === 'auto_contextual')))
                                                ? (i18n.t("flowBuilder.remarketing.modeContextual") || "💬 Contextual por Historial (Recomendado)")
                                                : (i18n.t("flowBuilder.remarketing.modeAngle") || "🎯 Ángulos de Venta (RAG)")}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: textSecondary, mt: 0.5, fontSize: '0.825rem', lineHeight: 1.45 }}>
                                            {(task.autoRemarketingType === 'contextual' || (task.content && task.content.some(c => c.type === 'auto_contextual')))
                                                ? (i18n.t("flowBuilder.remarketing.modeContextualDesc") || "La IA analiza la conversación reciente y continúa de forma humana y empática.")
                                                : (i18n.t("flowBuilder.remarketing.modeAngleDesc") || "La IA selecciona un ángulo de venta de la base de conocimiento (RAG).")}
                                        </Typography>
                                        {(task.promptGuidance || (task.content && task.content.find(c => c.type === 'auto_contextual')?.value)) && (
                                            <Box sx={{ mt: 1, p: 1, bgcolor: isDark ? 'rgba(0,0,0,0.3)' : '#FFFFFF', borderRadius: 1, borderLeft: `3px solid ${whitelabelColor}`, border: isDark ? 'none' : '1px solid #E2E8F0' }}>
                                                <Typography variant="caption" sx={{ color: textSecondary, display: 'block', fontWeight: 'bold' }}>
                                                    {i18n.t("flowBuilder.remarketing.guidanceLabel") || "Directriz del Asesor:"}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: textPrimary, fontSize: '0.85rem', fontStyle: 'italic', mt: 0.25 }}>
                                                    "{task.promptGuidance || task.content.find(c => c.type === 'auto_contextual')?.value}"
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                ) : (
                                    task.content && task.content.map((item, idx) => renderContentPreview(item, idx, true))
                                )}
                                {!task.content && !task.autoRemarketing && (
                                    <Typography variant="caption" color="error">
                                        Formato antiguo (solo lectura): Msg: {task.body} | Media: {task.mediaType}
                                    </Typography>
                                )}
                            </Box>
                        </Collapse>
                    </Paper>
                ))}
            </Stack>

            {/* Add New Task Form */}
            <Paper ref={editorRef} elevation={0} variant="outlined" sx={{ p: 2, borderRadius: 2, borderStyle: 'dashed', bgcolor: editorBg, borderColor: editingTaskId ? 'warning.main' : cardBorder }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: editingTaskId ? "#f59e0b" : whitelabelColor, display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                        <ScheduleIcon fontSize="small" style={{ color: editingTaskId ? "#f59e0b" : whitelabelColor }} />
                        {editingTaskId ? (i18n.t("flowBuilder.remarketing.editTask") || "Editando Tarea") : (i18n.t("flowBuilder.remarketing.newTask") || "Nueva Tarea")}
                    </Typography>
                    {(content.length > 0 || editingTaskId || isAutoRemarketing) && (
                        <Button
                            size="small"
                            variant="text"
                            onClick={resetEditor}
                            sx={{ color: '#ef4444', fontWeight: 'bold' }}
                        >
                            {editingTaskId ? i18n.t("globalModals.remarketing.cancelEdit") : i18n.t("globalModals.remarketing.clear")}
                        </Button>
                    )}
                </Box>

                {/* Settings Toggles */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    {enableAntiBan && !isAiNode && (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', p: 1.5, bgcolor: isAntiBan ? `${whitelabelColor}14` : (isDark ? 'transparent' : '#F8FAFC'), border: '1px solid', borderColor: isAntiBan ? `${whitelabelColor}4D` : cardBorder, borderRadius: 2 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isAntiBan}
                                        onChange={(e) => setIsAntiBan(e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: whitelabelColor,
                                                '& + .MuiSwitch-track': { backgroundColor: whitelabelColor }
                                            }
                                        }}
                                    />
                                }
                                label={
                                    <Typography variant="subtitle2" sx={{ color: isAntiBan ? whitelabelColor : textSecondary, fontWeight: 'bold' }}>
                                        🛡️ Anti-Ban (Reescribir con IA)
                                        {isAntiBan ? " [Activado]" : " [Desactivado]"}
                                    </Typography>
                                }
                            />
                        </Box>
                    )}

                    {isAiNode && (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', p: 1.5, bgcolor: isAutoRemarketing ? `${whitelabelColor}14` : (isDark ? 'transparent' : '#F8FAFC'), border: '1px solid', borderColor: isAutoRemarketing ? `${whitelabelColor}4D` : cardBorder, borderRadius: 2 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isAutoRemarketing}
                                        onChange={(e) => handleToggleAutoRemarketing(e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: whitelabelColor,
                                                '& + .MuiSwitch-track': { backgroundColor: whitelabelColor }
                                            }
                                        }}
                                    />
                                }
                                label={
                                    <Typography variant="subtitle2" sx={{ color: isAutoRemarketing ? whitelabelColor : textSecondary, fontWeight: 'bold' }}>
                                        🤖 Auto Remarketing
                                        {isAutoRemarketing ? " [Activado]" : " [Desactivado]"}
                                    </Typography>
                                }
                            />
                        </Box>
                    )}
                </Box>

                {/* Time Settings */}
                <Box sx={{ 
                    display: "flex", gap: 2, mb: 2, alignItems: "flex-start", flexWrap: "wrap",
                    "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: inputBorder },
                        "&:hover fieldset": { borderColor: isDark ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.4)" },
                        "&.Mui-focused fieldset": { borderColor: whitelabelColor },
                        color: textPrimary,
                        backgroundColor: inputBg,
                    },
                    "& .MuiInputLabel-root": { color: textSecondary },
                    "& .MuiInputLabel-root.Mui-focused": { color: whitelabelColor },
                    "& .MuiSelect-icon": { color: textSecondary },
                }}>
                    <TextField
                        label={i18n.t("flowBuilder.remarketing.waitTime") || "Espera"}
                        type="number"
                        size="small"
                        value={waitTime}
                        onChange={(e) => setWaitTime(Math.max(1, parseInt(e.target.value) || 1))}
                        sx={{ width: 100 }}
                        inputProps={{ min: 1 }}
                    />

                    <FormControl size="small" sx={{ width: 130 }}>
                        <InputLabel>{i18n.t("flowBuilder.remarketing.waitUnit") || "Tiempo"}</InputLabel>
                        <Select
                            value={waitUnit}
                            onChange={(e) => setWaitUnit(e.target.value)}
                            label={i18n.t("flowBuilder.remarketing.waitUnit") || "Tiempo"}
                        >
                            <MenuItem value="minutes">{i18n.t("flowBuilder.remarketing.units.minutes") || "Minutos"}</MenuItem>
                            <MenuItem value="hours">{i18n.t("flowBuilder.remarketing.units.hours") || "Horas"}</MenuItem>
                            <MenuItem value="days">{i18n.t("flowBuilder.remarketing.units.days") || "Días"}</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label={i18n.t("flowBuilder.remarketing.variableLabel") || "Variable Única"}
                        value={taskVariable}
                        onChange={handleTaskVariableChange}
                        onBlur={(e) => {
                            const val = e.target.value;
                            if (val && !validateUniqueVariable(val, tasks, editingTaskId)) {
                                setVariableError(true);
                            } else {
                                setVariableError(false);
                            }
                        }}
                        size="small"
                        variant="outlined"
                        style={{ width: '260px' }}
                        error={variableError}
                        helperText={variableError ? "Variable repetida" : ""}
                    />
                </Box>

                {isAutoRemarketing ? (
                    <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#F8FAFC', border: `1px solid ${cardBorder}` }}>
                        <Typography variant="subtitle2" sx={{ color: whitelabelColor, fontWeight: 'bold', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            🤖 {i18n.t("flowBuilder.remarketing.autoModeTitle") || "Modo de Auto-Remarketing"}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                            <Paper
                                onClick={() => handleChangeAutoType("contextual")}
                                elevation={0}
                                sx={{
                                    flex: 1,
                                    minWidth: '220px',
                                    p: 1.5,
                                    cursor: 'pointer',
                                    borderRadius: 2,
                                    border: '2px solid',
                                    borderColor: autoRemarketingType === 'contextual' ? whitelabelColor : cardBorder,
                                    bgcolor: autoRemarketingType === 'contextual' 
                                        ? `${whitelabelColor}1A` 
                                        : (isDark ? 'rgba(255,255,255,0.02)' : '#FFFFFF'),
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        borderColor: autoRemarketingType === 'contextual' ? whitelabelColor : (isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)'),
                                        bgcolor: autoRemarketingType === 'contextual' ? `${whitelabelColor}26` : (isDark ? 'rgba(255,255,255,0.05)' : '#F1F5F9')
                                    }
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ color: autoRemarketingType === 'contextual' ? whitelabelColor : textPrimary, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    💬 {i18n.t("flowBuilder.remarketing.modeContextual") || "Contextual por Historial (Recomendado)"}
                                </Typography>
                                <Typography variant="body2" sx={{ color: autoRemarketingType === 'contextual' ? textPrimary : textSecondary, display: 'block', mt: 0.75, fontSize: '0.825rem', lineHeight: 1.45, fontWeight: 400 }}>
                                    {i18n.t("flowBuilder.remarketing.modeContextualDesc") || "La IA analiza la conversación reciente y continúa de forma humana, empática y natural sin romper el contexto."}
                                </Typography>
                            </Paper>

                            <Paper
                                onClick={() => handleChangeAutoType("angle")}
                                elevation={0}
                                sx={{
                                    flex: 1,
                                    minWidth: '220px',
                                    p: 1.5,
                                    cursor: 'pointer',
                                    borderRadius: 2,
                                    border: '2px solid',
                                    borderColor: autoRemarketingType === 'angle' ? whitelabelColor : cardBorder,
                                    bgcolor: autoRemarketingType === 'angle' 
                                        ? `${whitelabelColor}1A` 
                                        : (isDark ? 'rgba(255,255,255,0.02)' : '#FFFFFF'),
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        borderColor: autoRemarketingType === 'angle' ? whitelabelColor : (isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)'),
                                        bgcolor: autoRemarketingType === 'angle' ? `${whitelabelColor}26` : (isDark ? 'rgba(255,255,255,0.05)' : '#F1F5F9')
                                    }
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ color: autoRemarketingType === 'angle' ? whitelabelColor : textPrimary, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    🎯 {i18n.t("flowBuilder.remarketing.modeAngle") || "Ángulos de Venta (RAG)"}
                                </Typography>
                                <Typography variant="body2" sx={{ color: autoRemarketingType === 'angle' ? textPrimary : textSecondary, display: 'block', mt: 0.75, fontSize: '0.825rem', lineHeight: 1.45, fontWeight: 400 }}>
                                    {i18n.t("flowBuilder.remarketing.modeAngleDesc") || "La IA selecciona un ángulo de venta de la base de conocimiento (RAG) para reactivar prospectos fríos a largo plazo."}
                                </Typography>
                            </Paper>
                        </Box>

                        {autoRemarketingType === 'contextual' ? (
                            <Box sx={{ mt: 1.5 }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    maxRows={4}
                                    variant="outlined"
                                    size="small"
                                    label={i18n.t("flowBuilder.remarketing.guidanceLabel") || "Directriz o gancho opcional para la IA"}
                                    placeholder={i18n.t("flowBuilder.remarketing.guidancePlaceholder") || "Ej: Pregúntale amablemente si pudo revisar los planes o si le quedó alguna duda técnica."}
                                    helperText={i18n.t("flowBuilder.remarketing.guidanceHelper") || "Opcional. Si lo dejas vacío, la IA deducirá cómo retomar según el historial."}
                                    value={promptGuidance}
                                    onChange={(e) => setPromptGuidance(e.target.value)}
                                    InputProps={{
                                        style: {
                                            color: textPrimary,
                                            backgroundColor: inputBg,
                                            borderRadius: '6px'
                                        }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: `${whitelabelColor}4D` },
                                            '&:hover fieldset': { borderColor: whitelabelColor },
                                            '&.Mui-focused fieldset': { borderColor: whitelabelColor }
                                        },
                                        '& .MuiInputLabel-root': { color: textSecondary },
                                        '& .MuiInputLabel-root.Mui-focused': { color: whitelabelColor },
                                        '& .MuiFormHelperText-root': { color: textSecondary, fontStyle: 'italic', fontSize: '0.75rem' }
                                    }}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: `${whitelabelColor}14`, border: `1px dashed ${whitelabelColor}66` }}>
                                <Typography variant="body2" sx={{ color: textPrimary, display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.825rem' }}>
                                    <InfoOutlinedIcon fontSize="small" sx={{ color: whitelabelColor }} />
                                    {i18n.t("remarketing.autoActiveDesc") || "La IA determinará el mejor mensaje según el historial y los ángulos de venta configurados en la Base de Conocimiento (RAG)."}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <>
                        <Typography variant="caption" sx={{ mb: 1, display: 'block', color: textSecondary }}>
                            {i18n.t("flowBuilder.remarketing.contentTitle") || "Contenido de la Tarea:"}
                        </Typography>

                        {/* Content Toolbar Buttons */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                            <Button
                                startIcon={<TextIcon />}
                                variant={isDark ? "contained" : "outlined"}
                                color="inherit"
                                size="small"
                                onClick={handleAddText}
                                sx={{ 
                                    bgcolor: isDark ? '#2D3239' : '#FFFFFF', 
                                    color: textPrimary,
                                    borderColor: cardBorder,
                                    '&:hover': {
                                        bgcolor: isDark ? '#373D45' : '#F1F5F9',
                                        borderColor: whitelabelColor
                                    },
                                    '&.Mui-disabled': { 
                                        color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', 
                                        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#F1F5F9' 
                                    } 
                                }}
                            >
                                {i18n.t("flowBuilder.remarketing.buttons.text") || "TEXTO"}
                            </Button>
                            <Button
                                startIcon={<IntervalIcon />}
                                variant={isDark ? "contained" : "outlined"}
                                color="inherit"
                                size="small"
                                onClick={handleInterval}
                                sx={{ 
                                    bgcolor: isDark ? '#2D3239' : '#FFFFFF', 
                                    color: textPrimary,
                                    borderColor: cardBorder,
                                    '&:hover': {
                                        bgcolor: isDark ? '#373D45' : '#F1F5F9',
                                        borderColor: whitelabelColor
                                    },
                                    '&.Mui-disabled': { 
                                        color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', 
                                        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#F1F5F9' 
                                    } 
                                }}
                            >
                                {i18n.t("flowBuilder.remarketing.buttons.interval") || "INTERVALO"}
                            </Button>
                            <Button
                                startIcon={<ImageIcon />}
                                variant={isDark ? "contained" : "outlined"}
                                color="inherit"
                                size="small"
                                component="label" 
                                sx={{ 
                                    bgcolor: isDark ? '#2D3239' : '#FFFFFF', 
                                    color: textPrimary,
                                    borderColor: cardBorder,
                                    '&:hover': {
                                        bgcolor: isDark ? '#373D45' : '#F1F5F9',
                                        borderColor: whitelabelColor
                                    },
                                    '&.Mui-disabled': { 
                                        color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', 
                                        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#F1F5F9' 
                                    } 
                                }}
                            >
                                {i18n.t("flowBuilder.remarketing.buttons.image") || "IMAGEN"}
                                <input type="file" hidden accept="image/*" ref={imageInputRef} onChange={(e) => handleFileUpload(e, 'image')} />
                            </Button>
                            <Button
                                startIcon={<VideoIcon />}
                                variant={isDark ? "contained" : "outlined"}
                                color="inherit"
                                size="small"
                                component="label" 
                                sx={{ 
                                    bgcolor: isDark ? '#2D3239' : '#FFFFFF', 
                                    color: textPrimary,
                                    borderColor: cardBorder,
                                    '&:hover': {
                                        bgcolor: isDark ? '#373D45' : '#F1F5F9',
                                        borderColor: whitelabelColor
                                    },
                                    '&.Mui-disabled': { 
                                        color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', 
                                        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#F1F5F9' 
                                    } 
                                }}
                            >
                                {i18n.t("flowBuilder.remarketing.buttons.video") || "VIDEO"}
                                <input type="file" hidden accept="video/*" ref={videoInputRef} onChange={(e) => handleFileUpload(e, 'video')} />
                            </Button>
                            <Button
                                startIcon={<AudioIcon />}
                                variant={isDark ? "contained" : "outlined"}
                                color="inherit"
                                size="small"
                                component="label" 
                                sx={{ 
                                    bgcolor: isDark ? '#2D3239' : '#FFFFFF', 
                                    color: textPrimary,
                                    borderColor: cardBorder,
                                    '&:hover': {
                                        bgcolor: isDark ? '#373D45' : '#F1F5F9',
                                        borderColor: whitelabelColor
                                    },
                                    '&.Mui-disabled': { 
                                        color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', 
                                        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#F1F5F9' 
                                    } 
                                }}
                            >
                                {i18n.t("flowBuilder.remarketing.buttons.audio") || "AUDIO"}
                                <input type="file" hidden accept="audio/*" ref={audioInputRef} onChange={(e) => handleFileUpload(e, 'audio')} />
                            </Button>
                            <Button
                                startIcon={<FileIcon />}
                                variant={isDark ? "contained" : "outlined"}
                                color="inherit"
                                size="small"
                                component="label" 
                                sx={{ 
                                    bgcolor: isDark ? '#2D3239' : '#FFFFFF', 
                                    color: textPrimary,
                                    borderColor: cardBorder,
                                    '&:hover': {
                                        bgcolor: isDark ? '#373D45' : '#F1F5F9',
                                        borderColor: whitelabelColor
                                    },
                                    '&.Mui-disabled': { 
                                        color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', 
                                        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#F1F5F9' 
                                    } 
                                }}
                            >
                                {i18n.t("flowBuilder.remarketing.buttons.doc") || "DOC"}
                                <input type="file" hidden accept=".pdf,.doc,.docx,.xls,.xlsx,.txt" ref={docInputRef} onChange={(e) => handleFileUpload(e, 'document')} />
                            </Button>
                        </Box>

                        {/* Loading Indicator */}
                        {loadingUpload && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2, justifyContent: 'center' }}>
                                <CircularProgress size={24} />
                                <Typography variant="body2" sx={{ color: textSecondary }}>Subiendo archivo...</Typography>
                            </Box>
                        )}

                        {/* Current Content List */}
                        <Box
                            key={`content-container-${tasks.length}-${content.length}`}
                            sx={{
                                mt: 1,
                                minHeight: content.length > 0 ? 'auto' : 80,
                                bgcolor: isDark ? '#23262B' : '#F8FAFC',
                                borderRadius: 1,
                                p: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: content.length > 0 ? 'flex-start' : 'center',
                                border: '1px solid',
                                borderColor: cardBorder
                            }}
                        >
                            {content.length === 0 ? (
                                <Typography variant="body2" sx={{ fontStyle: 'italic', color: textMuted }} align="center">
                                    {i18n.t("flowBuilder.remarketing.selectContentHelper") || "Seleccione un tipo de contenido arriba para comenzar a armar la tarea."}
                                </Typography>
                            ) : (
                                content.map((item, index) => renderContentPreview(item, index))
                            )}
                        </Box>
                    </>
                )}

                <Button
                    variant="contained"
                    fullWidth
                    color={editingTaskId ? "warning" : "primary"}
                    onClick={handleAddTask}
                    disabled={(!isAutoRemarketing && content.length === 0) || loadingUpload}
                    startIcon={editingTaskId ? <EditIcon /> : <AddIcon />}
                    sx={{ 
                        mt: 2,
                        ...(editingTaskId ? {} : {
                            bgcolor: whitelabelColor,
                            color: '#FFFFFF',
                            fontWeight: 'bold',
                            boxShadow: `0 4px 14px 0 ${whitelabelColor}66`,
                            '&:hover': {
                                bgcolor: whitelabelColor,
                                opacity: 0.9
                            }
                        }),
                        '&.Mui-disabled': {
                            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                            color: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.25)'
                        }
                    }}
                >
                    {editingTaskId ? (i18n.t("flowBuilder.remarketing.updateTask") || "Actualizar Tarea") : (i18n.t("flowBuilder.remarketing.saveTask") || "Guardar Tarea")}
                </Button>
            </Paper>
        </Box>
    );
});

export default React.memo(RemarketingTaskEditor);
